import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiRequestError } from "../../lib/api";
import type { Episode } from "./types";

interface CompletedUpload {
  assetId: string;
  sizeBytes: number;
  durationSeconds: number | null;
  durationExact: boolean;
}

async function uploadAudioFile(file: File, onProgress: (uploadedBytes: number) => void): Promise<{ episodeId: string; completed: CompletedUpload }> {
  const episodeId = crypto.randomUUID();
  const { uploadJobId, partSize } = await api.post<{ uploadJobId: string; partSize: number }>("/api/v1/podcast/uploads/audio/start", {
    episodeId,
    filename: file.name,
    sizeBytes: file.size
  });

  let partNumber = 1;
  let offset = 0;
  while (offset < file.size) {
    const chunk = file.slice(offset, offset + partSize);
    await api.putRaw(`/api/v1/podcast/uploads/audio/${uploadJobId}/part/${partNumber}`, chunk);
    offset += chunk.size;
    partNumber += 1;
    onProgress(offset);
  }

  const completed = await api.post<CompletedUpload>(`/api/v1/podcast/uploads/audio/${uploadJobId}/complete`);
  return { episodeId, completed };
}

function EpisodeUploadForm({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<{ uploaded: number; total: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!audioFile) return;
    setError(null);
    setSubmitting(true);
    setProgress({ uploaded: 0, total: audioFile.size });
    try {
      const { episodeId, completed } = await uploadAudioFile(audioFile, (uploaded) => setProgress({ uploaded, total: audioFile.size }));

      let coverAssetId: string | undefined;
      if (coverFile) {
        const form = new FormData();
        form.set("episodeId", episodeId);
        form.set("file", coverFile);
        const cover = await api.postForm<{ assetId: string }>("/api/v1/podcast/uploads/cover", form);
        coverAssetId = cover.assetId;
      }

      await api.post("/api/v1/podcast/episodes", {
        id: episodeId,
        title,
        description: description || undefined,
        audioAssetId: completed.assetId,
        coverAssetId,
        durationSeconds: completed.durationSeconds ?? undefined
      });

      setTitle("");
      setDescription("");
      setAudioFile(null);
      setCoverFile(null);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Upload fehlgeschlagen.");
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      {error && <div className="pw-alert pw-alert--error">{error}</div>}
      <div className="pw-field">
        <label>Titel</label>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
      </div>
      <div className="pw-field">
        <label>Beschreibung (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          style={{ padding: "0.65rem 0.8rem", borderRadius: "var(--pw-radius-sm)", border: "1px solid var(--pw-color-surface-border)", background: "var(--pw-color-surface)", color: "var(--pw-color-text)", fontFamily: "inherit", resize: "vertical" }}
        />
      </div>
      <div className="pw-field">
        <label>Audio-Datei (MP3, bis 250 MB)</label>
        <input type="file" accept="audio/mpeg,.mp3" required onChange={(e) => setAudioFile(e.target.files?.[0] ?? null)} />
      </div>
      <div className="pw-field">
        <label>Cover (optional, JPEG/PNG)</label>
        <input type="file" accept="image/jpeg,image/png" onChange={(e) => setCoverFile(e.target.files?.[0] ?? null)} />
      </div>
      {progress && (
        <p style={{ fontSize: "0.8rem", color: "var(--pw-color-text-muted)" }}>
          Lädt hoch: {Math.round((progress.uploaded / progress.total) * 100)} % ({(progress.uploaded / 1024 / 1024).toFixed(1)} / {(progress.total / 1024 / 1024).toFixed(1)} MB)
        </p>
      )}
      <button type="submit" className="pw-button pw-button--primary" disabled={submitting || !audioFile || !title}>
        {submitting ? "Wird hochgeladen …" : "Folge anlegen"}
      </button>
    </form>
  );
}

const STATUS_LABELS: Record<Episode["publishStatus"], string> = { draft: "Entwurf", published: "Veröffentlicht", withdrawn: "Zurückgezogen" };

export function PodcastAdminPage() {
  const [episodes, setEpisodes] = useState<Episode[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    api
      .get<{ episodes: Episode[] }>("/api/v1/podcast/episodes")
      .then((r) => setEpisodes(r.episodes))
      .catch(() => setError("Folgen konnten nicht geladen werden."));
  }, []);

  useEffect(() => reload(), [reload]);

  async function onPublish(id: string) {
    await api.post(`/api/v1/podcast/episodes/${id}/publish`);
    reload();
  }

  async function onWithdraw(id: string) {
    await api.post(`/api/v1/podcast/episodes/${id}/withdraw`);
    reload();
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <section className="pw-card">
        <h1 style={{ margin: "0 0 0.3rem", fontSize: "1.35rem", fontWeight: 600 }}>Podcast-Verwaltung</h1>
        <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--pw-color-text-muted)" }}>
          Neue Folgen werden als Entwurf angelegt und sind erst nach Veröffentlichung im Webplayer und im RSS-Feed sichtbar.
        </p>
      </section>

      <section className="pw-card">
        <h2 style={{ margin: "0 0 0.8rem", fontSize: "1.05rem", fontWeight: 600 }}>Neue Folge</h2>
        <EpisodeUploadForm onCreated={reload} />
      </section>

      {error && <div className="pw-alert pw-alert--error">{error}</div>}

      <section className="pw-card">
        <h2 style={{ margin: "0 0 0.8rem", fontSize: "1.05rem", fontWeight: 600 }}>Folgen</h2>
        {episodes?.length === 0 && <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--pw-color-text-muted)" }}>Noch keine Folgen.</p>}
        {episodes?.map((ep) => (
          <div key={ep.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.6rem 0", borderBottom: "1px solid var(--pw-color-surface-flat-border)" }}>
            <span style={{ flex: 1 }}>{ep.title}</span>
            <span style={{ fontSize: "0.75rem", color: "var(--pw-color-text-muted)" }}>{STATUS_LABELS[ep.publishStatus]}</span>
            {ep.publishStatus === "published" ? (
              <button type="button" className="pw-button" onClick={() => void onWithdraw(ep.id)}>
                Zurückziehen
              </button>
            ) : (
              <button type="button" className="pw-button pw-button--primary" onClick={() => void onPublish(ep.id)}>
                Veröffentlichen
              </button>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
